import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Balance, COPY, ExtraKind, ExtraSource, balanceView, daysUntil, endsIn, formatCents, isOut } from '../../lib/billing';
import { T } from './tokens';

/**
 * The credit meter in the task page's sidebar footer: a gauge to read while
 * building, never a link. As approved (Aima, 2026-10-20) and reviewed with
 * Samuel the same day:
 *   - The main row is the plan month, "Plan credits", with what is left
 *     ("62% left") or "100% used", and its blue bar. On the free trial it is
 *     "Free credits". It is always shown while there is a month to measure.
 *   - "Extra credits" is EVERY other credit that can be spent, not only
 *     top-ups: rolled-over plan credit, top-ups, referral rewards (earned, or
 *     for joining through a friend), promo credit (any code, link or staff
 *     grant: one line), the trial's leftover. One purple bar; the figure is
 *     what is left in dollars ("$42.40 left"): a top-up at the price paid,
 *     the rest at cost, the way a referral reward is priced. As a percent of
 *     the month a $1,000 top-up read "+1809%" (Aima, 2026-10-20).
 *   - With no plan and no trial (living on referral or promo credit), that
 *     credit is the only row, and it reads "Extra credits", never "Plan".
 *   - Hover (open or collapsed) shows the breakdown: each kind of extra
 *     credit with what is left in dollars, what of it ends within 30 days
 *     ("$5.00 ends in 12 days"), and which credit the next build spends
 *     first, by the ledger's own order. An older server without the dollars
 *     is read as a share of the month ("5% of a month").
 * No plan name on the meter: credits are credits, and the Plans page is where
 * a plan is bought or changed. Bars keep their colours (plan blue, extra
 * purple); low or out turns the figures amber.
 */

export const TOPUP_PURPLE = '#8b5cf6';
const EXTRA_TRACK = 'rgba(139,92,246,0.22)';
const MONTH_SHARE_WITH_EXTRA = 0.6;
const BAR_COLORS = { month: T.blue, extra: TOPUP_PURPLE };

/** The hover's name for a kind of extra credit, and the line under it where the name alone could be misread. */
export function extraLabel(kind: ExtraKind): { label: string; sub: string | null } {
  switch (kind) {
    case 'rollover': return { label: COPY.extraRollover, sub: COPY.extraRolloverSub };
    case 'topup': return { label: COPY.extraTopup, sub: null };
    case 'referral_earned': return { label: COPY.extraReferralEarned, sub: null };
    case 'referral_received': return { label: COPY.extraReferralReceived, sub: null };
    case 'free': return { label: COPY.extraFree, sub: null };
    case 'promo': case 'admin': case 'beta': return { label: COPY.extraPromo, sub: null };
    default: return { label: String(kind), sub: null };
  }
}

/** Say an end only when it is near enough to plan around. */
export const ENDING_WITHIN_DAYS = 30;

/** "ends in 12 days" when the whole line ends then; "$5.00 ends in 12 days" when part of it does (a share of the month from an older server). */
export function endingLine(s: ExtraSource, now: number): string | null {
  const e = s.ending;
  if (!e || !e.at || e.micros <= 0) return null;
  const days = daysUntil(e.at, now);
  if (days > ENDING_WITHIN_DAYS) return null;
  const when = endsIn(days);
  if (e.micros >= s.remainingMicros) return when;
  const cents = e.usdCents != null ? e.usdCents : s.kind === 'topup' ? e.retailCents : undefined;
  const amount = cents != null ? formatCents(cents) : `${e.pct}%`;
  return `${amount} ${when}`;
}

/** One part of the bar: its share of the ring and how much of it is spent (0-1). */
export interface MeterPart { share: number; used: number }

export interface MeterView {
  /** Main row: "Plan credits" or "Free credits", its figure and how much is spent; null when there is no month. */
  main: { label: string; figure: string; used: number } | null;
  /** Extra row, when there is extra credit (or a spent top-up still on the account): "Extra credits" · "+34%". */
  extra: { label: string; figure: string; used: number } | null;
  /** The hover card's figures. */
  card: {
    main: { label: string; usedPct: number; leftPct: number } | null;
    extra: { left: string; sources: SourceLine[] } | null;
  };
  /** Which credit the next build spends first. */
  active: 'main' | 'extra' | null;
  /** What the status line says about it. */
  activeText: string;
  /** The ring's (and the dashboard's) parts, in spending order; null = no bar. */
  bar: { month: MeterPart | null; extra: MeterPart | null } | null;
  warn: boolean;
  /** Collapsed hover: the credit being spent now and what is left of it. */
  hover: string;
  /** Open hover: the figures, one line each. */
  details: string[];
  /** One sentence for screen readers. */
  spoken: string;
}

const clamp01 = (n: number) => Math.max(0, Math.min(1, n));

/** One line of the hover card's breakdown. */
export interface SourceLine { key: string; kind: ExtraKind; label: string; left: string; detail: string | null; ending: string | null }

/** What is left of one kind, in dollars (a top-up at the price paid, the rest at cost); a share of the month from an older server. */
function sourceLine(s: ExtraSource, hasMonth: boolean, now: number): SourceLine {
  const { label, sub } = extraLabel(s.kind);
  const ending = endingLine(s, now);
  const cents = s.usdCents != null ? s.usdCents : s.kind === 'topup' ? s.retailCents : undefined;
  const left = cents != null ? `${formatCents(cents)} left` : hasMonth ? `${s.pct}% of a month` : `${s.pct}% left`;
  if (s.kind === 'topup') return { key: s.kind, kind: s.kind, label, left, detail: s.purchasedRetailCents ? `${formatCents(s.purchasedRetailCents)} bought` : null, ending };
  return { key: s.kind, kind: s.kind, label, left, detail: sub, ending };
}

export function meterView(b: Balance, now: number = Date.now()): MeterView {
  const u = b.usage;
  const warn = !!b.low || isOut(b);
  const basis = u ? u.basis : null;
  const pct = u && u.planPctUsed != null ? Math.max(0, Math.min(100, u.planPctUsed)) : null;
  const hasMonth = basis === 'plan' || basis === 'trial';
  const mainLabel = basis === 'trial' ? COPY.freeCredits : COPY.planCredits;
  // On the 'credit' basis the extra credit IS the measured row; it is drawn as the extra row below.
  const main = pct == null || !hasMonth ? null : { label: mainLabel, figure: pct < 100 ? `${100 - pct}% left` : '100% used', used: pct / 100 };

  // Extra credit: from the server's breakdown; an older server only says top-ups.
  const ex = u && u.extra ? u.extra : null;
  const topupCents = (u && u.topupRetailCents) || 0;
  const topupBought = (u && u.topupPurchasedRetailCents) || 0;
  let extra: MeterView['extra'] = null;
  let sources: NonNullable<MeterView['card']['extra']>['sources'] = [];
  let extraLeftText = '';
  if (ex && (ex.remainingMicros > 0 || ex.amountMicros > 0)) {
    sources = ex.bySource.map((s) => sourceLine(s, hasMonth, now));
    // Dollars when the server says them; a share of the month from an older server.
    const dollars = ex.usdCents != null && ex.remainingMicros > 0 ? `${formatCents(ex.usdCents)} left` : null;
    if (basis === 'credit' && pct != null) {
      // No month: this credit is the 100%, and its own used share is the bar.
      extraLeftText = dollars || (pct < 100 ? `${100 - pct}% left` : '100% used');
      extra = { label: COPY.extraCredits, figure: extraLeftText, used: pct / 100 };
    } else {
      extraLeftText = dollars || (ex.remainingMicros > 0 ? `+${ex.pct}%` : 'used up');
      extra = { label: COPY.extraCredits, figure: extraLeftText, used: ex.usedShare };
    }
  } else if (!ex && (topupCents > 0 || topupBought > 0)) {
    // Older server: top-ups only, in dollars, as before.
    extraLeftText = `${formatCents(topupCents)} left`;
    const used = topupBought > 0 ? clamp01(1 - topupCents / topupBought) : 0;
    extra = { label: COPY.extraCredits, figure: extraLeftText, used };
    sources = [{ key: 'topup', kind: 'topup', label: COPY.extraTopup, left: extraLeftText, detail: topupBought ? `${formatCents(topupBought)} bought` : null, ending: null }];
  }
  // An older server says nothing about what a top-up cost: the row shows, the bar does not (as before).
  const extraBar = !!extra && (ex ? true : topupBought > 0);

  // What the next build spends: the ledger's order when the server says, else the main row while it has credit.
  const spending = u && u.spendingNow !== undefined ? u.spendingNow : null;
  const active: MeterView['active'] = spending === 'plan' || spending === 'free'
    ? 'main'
    : spending
      ? 'extra'
      : main && pct! < 100 ? 'main' : extra && ex ? (ex.remainingMicros > 0 ? 'extra' : null) : extra && topupCents > 0 ? 'extra' : null;
  const activeText = active === 'main' ? (basis === 'trial' ? COPY.usingFree : COPY.usingPlan) : active === 'extra' ? COPY.usingExtra : COPY.noCreditsLeft;

  const both = !!main && extraBar;
  const bar: MeterView['bar'] = main || extraBar
    ? {
      month: main ? { share: both ? MONTH_SHARE_WITH_EXTRA : 1, used: main.used } : null,
      extra: extraBar ? { share: both ? 1 - MONTH_SHARE_WITH_EXTRA : 1, used: extra!.used } : null,
    }
    : null;

  const hover = active === 'main' && main
    ? `${main.label} · ${main.figure}`
    : active === 'extra' && extra
      ? `${COPY.extraCredits} · ${extraLeftText}`
      : main ? `${main.label} · 100% used` : COPY.noCreditsLeft;

  const details: string[] = [];
  if (main) details.push(`${main.label}: ${pct}% used, ${100 - pct!}% left`);
  if (extra) details.push(`${COPY.extraCredits}: ${extraLeftText}${sources.length ? ` (${sources.map((s) => `${s.label} ${s.left}${s.ending ? `, ${s.ending}` : ''}`).join('; ')})` : ''}`);
  details.push(active ? `${activeText} now` : COPY.noCreditsLeft);

  const card: MeterView['card'] = {
    main: main && pct != null ? { label: main.label, usedPct: pct, leftPct: 100 - pct } : null,
    extra: extra ? { left: extraLeftText, sources } : null,
  };

  const spoken = `${details.join('. ')}.`;
  return { main, extra, card, active, activeText, bar, warn, hover, details, spoken };
}

/**
 * The hover surface, portalled to <body> (the sidebar is overflow:hidden and
 * clipped anything placed inside it). Same card as the app's menus: #1a1a1a,
 * a hairline border, a soft shadow, DM Sans.
 */
const Tip: React.FC<{ at: { x: number; y: number; above?: boolean }; children: React.ReactNode; wide?: boolean }> = ({ at, children, wide }) => (
  typeof document === 'undefined' ? null : createPortal(
    <span
      role="tooltip"
      data-testid="task-credit-tooltip"
      className={`pointer-events-none fixed z-[1000] block rounded-[10px] text-xs ${at.above ? '-translate-y-full' : '-translate-y-1/2'} ${wide ? 'w-[260px] p-3' : 'whitespace-nowrap px-2.5 py-1.5 font-medium'}`}
      style={{
        left: at.x, top: at.y, color: T.t1, fontFamily: "'DM Sans', system-ui, sans-serif",
        background: T.surface2, border: `1px solid ${T.borderRegular}`, boxShadow: '0 12px 32px rgba(0,0,0,0.45)',
      }}
    >
      {children}
    </span>,
    document.body,
  )
);

/** One figure line in the card: label left, value right. `strong` is a row heading; `named` is a source (Top-up, Rolled over…): white like a heading, regular weight, so it stands off its dim sub-lines without competing with the headings. */
const CardLine: React.FC<{ label: string; value: string; strong?: boolean; named?: boolean; testId?: string; sub?: string | null; ending?: string | null }> = ({ label, value, strong, named, testId, sub, ending }) => (
  <span className="block" data-testid={testId}>
    <span className="flex items-baseline justify-between gap-3">
      <span style={{ color: strong || named ? T.t1 : T.t3 }} className={`min-w-0 truncate ${strong ? 'font-medium' : ''}`}>{label}</span>
      <span className={`shrink-0 tabular-nums ${strong ? 'font-semibold' : ''}`} style={{ color: strong ? T.t1 : T.t2 }}>{value}</span>
    </span>
    {sub && <span className="block text-[11px]" style={{ color: T.t3 }}>{sub}</span>}
    {ending && <span className="block text-[11px] tabular-nums" style={{ color: T.t2 }} data-testid="task-credit-card-ending">{ending}</span>}
  </span>
);

const CardBar: React.FC<{ used: number; fill: string; track: string }> = ({ used, fill, track }) => (
  <span className="my-1.5 flex h-[3px]"><BarPart part={{ share: 1, used }} track={track} fill={fill} testId="task-credit-card-bar" /></span>
);

/** The open meter's hover card: each credit, what it is made of, and which one the next build spends. */
const UsageCard: React.FC<{ m: MeterView }> = ({ m }) => {
  const dot = m.active === 'main' ? BAR_COLORS.month : m.active === 'extra' ? BAR_COLORS.extra : T.warn;
  const { main, extra } = m.card;
  return (
    <>
      <span className="flex items-center justify-between gap-3">
        <span className="text-[12.5px] font-semibold" style={{ color: T.t1 }}>Your credit</span>
        <span className="flex items-center gap-1.5 text-[11px]" style={{ color: T.t2 }} data-testid="task-credit-card-status">
          <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full" style={{ background: dot }} />
          {m.activeText}
        </span>
      </span>
      {main && (
        <span className="mt-3 block" data-testid="task-credit-card-plan">
          <CardLine label={main.label} value={`${main.usedPct}% used`} strong />
          <CardBar used={main.usedPct / 100} fill={BAR_COLORS.month} track={T.borderSubtle} />
          <CardLine label="Left" value={`${main.leftPct}%`} />
        </span>
      )}
      {extra && (
        <span className={`block ${main ? 'mt-3 border-t pt-3' : 'mt-3'}`} style={{ borderColor: T.borderSubtle }} data-testid="task-credit-card-extra">
          <CardLine label={COPY.extraCredits} value={extra.left} strong />
          {m.extra && <CardBar used={m.extra.used} fill={BAR_COLORS.extra} track={EXTRA_TRACK} />}
          <span className="block space-y-1" data-testid="task-credit-card-sources">
            {extra.sources.map((s) => <CardLine key={s.key} named label={s.label} value={s.left} sub={s.detail} ending={s.ending} testId={`task-credit-card-source-${s.kind}`} />)}
          </span>
        </span>
      )}
    </>
  );
};

/** The rail's ring: a button that opens the sidebar, its hover names the credit being spent now. */
const CollapsedRing: React.FC<{ m: MeterView; onExpand?: () => void }> = ({ m, onExpand }) => {
  const ref = useRef<HTMLButtonElement>(null);
  const [at, setAt] = useState<{ x: number; y: number } | null>(null);
  const show = () => {
    const rect = ref.current && ref.current.getBoundingClientRect();
    if (rect) setAt({ x: rect.right + 8, y: rect.top + rect.height / 2 });
  };
  const hide = () => setAt(null);
  return (
    <>
      <button
        ref={ref}
        type="button"
        onClick={() => { hide(); if (onExpand) onExpand(); }}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        aria-label={`${m.spoken} Open the sidebar to see more.`}
        data-testid="task-credit-meter"
        data-warn={m.warn ? 'true' : 'false'}
        className="mx-auto flex h-11 w-11 items-center justify-center rounded-lg hover:bg-white/[0.04] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3264F8]"
      >
        <svg width="28" height="28" viewBox="0 0 28 28" aria-hidden="true">
          <CreditRingArcs m={m} testIdPrefix="task-credit" />
          {m.warn && <text x="14" y="18.5" textAnchor="middle" fontSize="12" fontWeight="700" fill={T.warn}>!</text>}
        </svg>
      </button>
      {at && <Tip at={at}>{m.hover}</Tip>}
    </>
  );
};

/** One part of the bar: its own track (grey for the plan, faint purple for the extra) and its fill. */
const BarPart: React.FC<{ part: MeterPart; track: string; fill: string; testId: string }> = ({ part, track, fill, testId }) => (
  <span className="block h-full overflow-hidden rounded" style={{ width: `${part.share * 100}%`, background: track }}>
    <span
      data-testid={testId}
      className="block h-full rounded transition-[width] duration-300 motion-reduce:transition-none"
      style={{ width: `${Math.round(part.used * 100)}%`, background: fill }}
    />
  </span>
);

/**
 * The main sidebar's (dashboard) bars: the month's blue bar and under it the
 * extra credit's purple bar, each full width and filling with what of it is spent.
 */
export const CreditBar: React.FC<{ m: MeterView; testIdPrefix: string; className?: string }> = ({ m, testIdPrefix, className = 'mt-1.5' }) => {
  if (!m.bar) return null;
  const full = (p: MeterPart): MeterPart => ({ ...p, share: 1 });
  return (
    <span aria-hidden="true" data-testid={`${testIdPrefix}-bar`} className={`${className} flex flex-col gap-1.5`}>
      {m.bar.month && (
        <span className="flex h-[3px]"><BarPart part={full(m.bar.month)} track={T.borderSubtle} fill={BAR_COLORS.month} testId={`${testIdPrefix}-bar-month`} /></span>
      )}
      {m.bar.extra && (
        <span className="flex h-[3px]"><BarPart part={full(m.bar.extra)} track={EXTRA_TRACK} fill={BAR_COLORS.extra} testId={`${testIdPrefix}-bar-extra`} /></span>
      )}
    </span>
  );
};

/**
 * The same two parts round a 28px ring (r 11, centre 14,14): blue from 12
 * o'clock, purple carrying on after it. Goes inside an <svg viewBox="0 0 28 28">.
 */
export const CreditRingArcs: React.FC<{ m: MeterView; testIdPrefix: string }> = ({ m, testIdPrefix }) => {
  const r = 11;
  const c = 2 * Math.PI * r;
  if (!m.bar) return <circle cx="14" cy="14" r={r} fill="none" stroke={m.warn ? T.warn : T.borderRegular} strokeWidth="3" />;
  const { month, extra } = m.bar;
  const monthLen = month ? c * month.share : 0;
  // An arc of `len` starting `from` along the circle (from 12 o'clock, clockwise).
  const arc = (from: number, len: number, stroke: string, testId?: string) => (
    <circle
      data-testid={testId}
      cx="14" cy="14" r={r} fill="none" stroke={stroke} strokeWidth="3"
      strokeDasharray={`${Math.max(0, len)} ${c}`} strokeDashoffset={-from}
      transform="rotate(-90 14 14)"
      className="transition-[stroke-dasharray] duration-300 motion-reduce:transition-none"
    />
  );
  return (
    <>
      {month && arc(0, monthLen, T.borderRegular)}
      {extra && arc(monthLen, c * extra.share, EXTRA_TRACK)}
      {month && arc(0, monthLen * month.used, BAR_COLORS.month, `${testIdPrefix}-ring-month`)}
      {extra && arc(monthLen, c * extra.share * extra.used, BAR_COLORS.extra, `${testIdPrefix}-ring-extra`)}
    </>
  );
};

/** One row of the open meter: label and figure on a line, its thin bar under. */
const Row: React.FC<{ label: string; figure: string; used: number; fill: string; track: string; warn: boolean; id: 'plan' | 'extra'; first?: boolean }> = ({ label, figure, used, fill, track, warn, id, first }) => (
  <span aria-hidden="true" className={`block ${first ? '' : 'mt-2.5'}`} data-testid={`task-credit-${id}`}>
    <span className="flex items-baseline justify-between gap-2">
      <span data-testid={`task-credit-${id}-label`} className="truncate text-xs" style={{ color: T.t2 }}>{label}</span>
      <span data-testid={`task-credit-${id}-figure`} className="shrink-0 text-[12.5px] font-semibold tabular-nums" style={{ color: warn ? T.warn : T.t1 }}>{figure}</span>
    </span>
    <span className="mt-1.5 flex h-[2px]">
      <BarPart part={{ share: 1, used }} track={track} fill={fill} testId={`task-credit-${id}-bar`} />
    </span>
  </span>
);

const TaskCreditMeter: React.FC<Props> = ({ balance, collapsed = false, onExpand, plansTo, onNavigate }) => {
  const m = meterView(balance);
  const link = plansLink(balance, plansTo, onNavigate);
  const ref = useRef<any>(null);
  const [at, setAt] = useState<{ x: number; y: number; above: boolean } | null>(null);
  const show = () => {
    const rect = ref.current && ref.current.getBoundingClientRect();
    if (rect) setAt({ x: rect.left, y: rect.top - 6, above: true });
  };
  const hide = () => setAt(null);

  // Collapsed, the ring only opens the sidebar, on every page; the Plans link is the open meter's.
  if (collapsed) return <CollapsedRing m={m} onExpand={onExpand} />;
  if (!m.main && !m.extra && !link) return null;

  return React.createElement(
    (link ? Link : 'div') as any,
    {
      ref,
      ...(link ? { to: link.href, onClick: link.onNavigate } : { role: 'group', tabIndex: 0 }),
      'aria-label': link ? `${m.spoken} ${link.cta}.` : m.spoken,
      onMouseEnter: show,
      onMouseLeave: hide,
      onFocus: show,
      onBlur: hide,
      'data-testid': 'task-credit-meter',
      'data-warn': m.warn ? 'true' : 'false',
      className: `block w-full rounded-lg px-2 py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3264F8]${link ? ' hover:bg-white/[0.04]' : ''}`,
    },
    <>
      {m.main && (
        <Row id="plan" first label={m.main.label} figure={m.main.figure} used={m.main.used} fill={BAR_COLORS.month} track={T.borderSubtle} warn={m.warn && m.active !== 'extra'} />
      )}
      {m.extra && (
        <Row id="extra" first={!m.main} label={m.extra.label} figure={m.extra.figure} used={m.extra.used} fill={BAR_COLORS.extra} track={EXTRA_TRACK} warn={m.warn && m.active === 'extra'} />
      )}
      {link && (
        <span aria-hidden="true" data-testid="task-credit-cta" className="mt-2 block text-xs leading-snug" style={{ color: m.warn ? T.warn : T.t2 }}>
          <span className="underline underline-offset-2">{link.cta}</span>
        </span>
      )}
      {at && (
        <Tip at={at} wide><UsageCard m={m} /></Tip>
      )}
    </>,
  );
};

/** The dashboard's Plans link: "Get more" opens the top-up sheet (?topup=1), "See plans" the page. */
interface PlansLink { href: string; cta: string; onNavigate?: () => void }
function plansLink(balance: Balance, to: string | undefined, onNavigate?: () => void): PlansLink | null {
  if (!to) return null;
  const cta = balanceView(balance).cta;
  const href = cta === 'Get more' ? `${to}${to.includes('?') ? '&' : '?'}topup=1` : to;
  return { href, cta, onNavigate };
}

interface Props {
  balance: Balance;
  /** The 56px rail: a ring only, the credit being spent in its tooltip. */
  collapsed?: boolean;
  /** Collapsed: clicking the ring opens the sidebar, where the full meter shows. */
  onExpand?: () => void;
  /**
   * The dashboard: the meter is the link to Plans, as its balance row always
   * was ("See plans" / "Get more" under it). Collapsed, the ring still just
   * opens the sidebar.
   * Absent on the task page, where the meter is only read.
   */
  plansTo?: string;
  onNavigate?: () => void;
}

export default TaskCreditMeter;
